import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Signal,
  computed,
  effect,
  inject,
} from '@angular/core';
import { FLOATING_PANEL_DATA } from '@shared/floating-panel/floating-panel-data.token';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../bodygraph/hd-gate-info.service';
import { HdPanelSectionScroller } from '../bodygraph/hd-panel-section-scroller';
import { HdEmotionalWavesListComponent } from './hd-emotional-waves-list.component';
import { HdEmotionalWavesPanelData } from './hd-emotional-waves.metadata';

/**
 * Golvenpaneel: titel + {@link HdEmotionalWavesListComponent}. Geopend via
 * `HdEmotionalWavesPanelService` vanaf de golfregel in het poort-infoscherm;
 * een `focusWave` in de paneel-data laat het paneel na render naar die golf
 * scrollen (ook bij hergebruik van een al open paneel).
 */
@Component({
  selector: 'hd-emotional-waves-panel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdEmotionalWavesListComponent],
  template: `
    <header class="hd-emotional-waves-panel__header">
      <h3 class="hd-emotional-waves-panel__title">{{ title() }}</h3>
    </header>
    <hd-emotional-waves-list [definedChannelKeys]="definedChannelKeys()" />
  `,
  styles: `
    /* Breedtecap op :host zodat de mobiele sheet-override van de
       floating-panel-host (100% breed) automatisch wint. */
    :host {
      display: block;
      width: min(540px, 70vw);
      color: #3d414e;
    }

    .hd-emotional-waves-panel__header {
      margin-bottom: 6px;
    }

    .hd-emotional-waves-panel__title {
      margin: 0;
      font-family: 'Bw Modelica', 'Raleway', sans-serif;
      font-weight: 900;
      font-size: 18px;
      line-height: 1.25;
    }
  `,
})
export class HdEmotionalWavesPanelComponent implements AfterViewChecked {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  private readonly panelData = inject(FLOATING_PANEL_DATA, { optional: true }) as
    | Signal<HdEmotionalWavesPanelData>
    | null;

  /** Auto-scroll naar de focus-golf, gedeeld met de fears- en voices-panelen. */
  private readonly scroller = new HdPanelSectionScroller(this.host.nativeElement, this.cdr);

  protected readonly definedChannelKeys = computed(() => this.panelData?.().definedChannelKeys);

  protected readonly title = computed(() => {
    const lang = this.contentLang.effectiveLang();
    return this.gateInfo.cheatSheetTextIn('emotionalWaves', 'panel.title', lang)
      || this.gateInfo.cheatSheetTextIn('emotionalWaves', 'panel.title', 'nl');
  });

  constructor() {
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');

    // Elke (her)aanlevering van paneel-data met een focus-golf wapent de
    // auto-scroll opnieuw; scrollen gebeurt in ngAfterViewChecked zodra de
    // sectie gerenderd is en de scope-tekst geladen is.
    effect(() => {
      const focusWave = this.panelData?.().focusWave ?? null;
      if (focusWave != null) {
        this.scroller.arm(`[data-emotional-wave="${focusWave}"]`, () => this.title() !== '');
      }
    });

    this.destroyRef.onDestroy(() => this.scroller.dispose());
  }

  ngAfterViewChecked(): void {
    this.scroller.onViewChecked();
  }
}
